import React, { useState, useEffect } from 'react';
import { X, Download, FolderOpen, FolderPlus, ChevronLeft, Check, Loader, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import folderService from '../../services/folderServices';

const FOLDER_COLORS = [
  '#6a1fbf', '#7700FF', '#0E72ED', '#00c853',
  '#f59e0b', '#ef4444', '#ec4899', '#14b8a6',
];

/**
 * GetSharedElementModal
 * Flow:
 *   1) user enters a share code → server is asked whether the code matches
 *      a single element OR a whole shared folder (auto-detected)
 *   2a) element match → user picks an existing folder OR creates a new one,
 *       then we import the element into it
 *   2b) folder match  → server creates a new folder under the user's account
 *       with the same name (collision-suffixed if needed) and clones every
 *       element inside; user just confirms the import
 *
 * The original element/folder keeps its share code — recipients receive
 * independent copies, not live references.
 *
 * Props:
 *   isOpen     — boolean
 *   onClose    — callback()
 *   onSuccess  — callback(folderId) — called after successful copy
 */
export default function GetSharedElementModal({ isOpen, onClose, onSuccess }) {
  // 'code' | 'choose-folder' | 'new-folder' | 'folder-confirm'
  const [step, setStep] = useState('code');
  const [code, setCode] = useState('');
  const [looking, setLooking] = useState(false);
  const [preview, setPreview] = useState(null);
  // 'element' | 'folder' — which kind matched the entered code, drives
  // whether the next step is the destination-folder picker (element) or
  // the single-button confirm (folder).
  const [previewKind, setPreviewKind] = useState('element');
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [copying, setCopying] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6a1fbf');
  const [creatingFolder, setCreatingFolder] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep('code');
    setCode('');
    setPreview(null);
    setPreviewKind('element');
    setSelectedFolderId(null);
    setNewFolderName('');
    setNewFolderColor('#6a1fbf');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLookup = async (e) => {
    e?.preventDefault?.();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      toast.error('Please enter a share code');
      return;
    }
    setLooking(true);
    try {
      // Element first (most common case). A 404 here is normal when the
      // code belongs to a folder, so swallow lookup errors and fall through.
      let elementHit = null;
      try {
        const elRes = await folderService.getElementByShareCode(trimmed);
        if (elRes?.element) elementHit = elRes.element;
      } catch (_) { /* not an element — try folder next */ }

      if (elementHit) {
        setPreviewKind('element');
        setPreview(elementHit);
        setLoadingFolders(true);
        try {
          const foldersRes = await folderService.getFolders();
          const list = Array.isArray(foldersRes) ? foldersRes : [];
          setFolders(list);
          if (list.length > 0) {
            setSelectedFolderId(list[0]._id);
            setStep('choose-folder');
          } else {
            // No folders yet — jump straight to create-folder view
            setStep('new-folder');
          }
        } catch (_) {
          setFolders([]);
          setStep('new-folder');
        } finally {
          setLoadingFolders(false);
        }
        return;
      }

      // Fall back to folder lookup. The service returns null on 404 so the
      // "No item for this code" branch fires cleanly without a thrown error.
      const folderRes = await folderService.getFolderByShareCode(trimmed);
      if (folderRes?.folder) {
        setPreviewKind('folder');
        setPreview(folderRes.folder);
        setStep('folder-confirm');
        return;
      }

      toast.error('No element or folder found for that code');
    } catch (err) {
      toast.error(err?.message || 'Invalid or unknown share code');
    } finally {
      setLooking(false);
    }
  };

  const handleImportFolder = async () => {
    setCopying(true);
    try {
      const res = await folderService.copyFolderByShareCode(code.trim().toUpperCase());
      const newFolder = res?.folder;
      const elementCount = res?.elementCount ?? 0;
      const noun = elementCount === 1 ? 'element' : 'elements';
      toast.success(`"${newFolder?.name}" imported with ${elementCount} ${noun}`);
      onSuccess?.(newFolder?.id);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to import folder');
    } finally {
      setCopying(false);
    }
  };

  const copyIntoFolder = async (folderId, folderName) => {
    setCopying(true);
    try {
      await folderService.copyElementByShareCode(code.trim().toUpperCase(), folderId);
      toast.success(`"${preview?.name}" copied into "${folderName || 'folder'}"`);
      onSuccess?.(folderId);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to copy element');
    } finally {
      setCopying(false);
    }
  };

  const handleCopy = () => {
    if (!selectedFolderId) {
      toast.error('Please choose a folder');
      return;
    }
    const folder = folders.find((f) => f._id === selectedFolderId);
    copyIntoFolder(selectedFolderId, folder?.name);
  };

  const handleCreateFolderAndCopy = async () => {
    const name = newFolderName.trim();
    if (!name) {
      toast.error('Please enter a folder name');
      return;
    }
    setCreatingFolder(true);
    try {
      const res = await folderService.createFolder({ name, color: newFolderColor });
      const folder = res?.folder || res;
      const folderId = folder?._id || folder?.id;
      if (!folderId) {
        throw new Error('Folder could not be created');
      }
      setFolders((prev) => [...prev, folder]);
      setCreatingFolder(false);
      await copyIntoFolder(folderId, name);
    } catch (err) {
      setCreatingFolder(false);
      toast.error(err?.message || 'Failed to create folder');
    }
  };

  const busy = copying || creatingFolder;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-[#0d0d1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-purple-400" />
            <h2 className="text-base font-semibold text-white">Add Shared Elements</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {step === 'code' && (
          <form onSubmit={handleLookup} className="px-6 py-5">
            <label className="block text-sm text-gray-300 mb-2">
              Enter the share code someone sent you:
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. K7F2H9P4"
              maxLength={20}
              autoFocus
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-mono tracking-[0.25em] text-center text-lg placeholder-gray-600 outline-none focus:border-[#6a1fbf] transition-colors"
            />
            <p className="text-xs text-gray-500 mt-2">
              A copy of the shared element or folder will be added to your
              account. You'll choose a destination on the next step (or, for
              shared folders, a new folder is created automatically).
            </p>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={looking || !code.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {looking ? <Loader className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {looking ? 'Looking up…' : 'Continue'}
              </button>
            </div>
          </form>
        )}

        {step === 'folder-confirm' && (
          <>
            <div className="px-6 pt-4 pb-2">
              <p className="text-xs text-gray-400 mb-1">Found shared folder:</p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
                <FolderOpen
                  className="w-3.5 h-3.5"
                  style={{ color: preview?.color || '#6a1fbf' }}
                />
                <span className="text-sm text-white font-medium truncate max-w-[300px]">
                  {preview?.name}
                </span>
                <span className="text-xs text-purple-300 px-1.5 py-0.5 rounded bg-purple-500/20">
                  {preview?.elementCount ?? 0}{' '}
                  {(preview?.elementCount ?? 0) === 1 ? 'element' : 'elements'}
                </span>
              </div>
            </div>

            <div className="px-6 pb-4 min-h-[160px]">
              <p className="text-sm text-gray-300 mb-3">
                A new folder named{' '}
                <span className="text-white font-medium">"{preview?.name}"</span>{' '}
                will be created in your account with all{' '}
                {preview?.elementCount ?? 0} element
                {(preview?.elementCount ?? 0) === 1 ? '' : 's'} copied inside it.
                If you already have a folder with that name, your copy will be
                suffixed automatically (e.g. "{preview?.name} (2)").
              </p>
              <p className="text-xs text-gray-500">
                The recipient owns the copy — future changes the original
                author makes won't sync to your version.
              </p>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setStep('code')}
                disabled={copying}
                className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleImportFolder}
                disabled={copying}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copying ? <Loader className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
                {copying ? 'Importing…' : 'Import Folder'}
              </button>
            </div>
          </>
        )}

        {(step === 'choose-folder' || step === 'new-folder') && (
          <>
            <div className="px-6 pt-4 pb-2">
              <p className="text-xs text-gray-400 mb-1">Found element:</p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
                <Download className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-sm text-white font-medium truncate max-w-[300px]">
                  {preview?.name}
                </span>
                {preview?.type && (
                  <span className="text-xs text-purple-300 px-1.5 py-0.5 rounded bg-purple-500/20">
                    {preview.type}
                  </span>
                )}
              </div>
            </div>

            <div className="px-6 pb-2 min-h-[160px]">
              {step === 'choose-folder' && (
                <>
                  <p className="text-sm text-gray-300 mb-3">Copy into which folder?</p>
                  {loadingFolders ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader className="w-5 h-5 text-purple-400 animate-spin" />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {folders.map((folder) => (
                          <button
                            key={folder._id}
                            onClick={() => setSelectedFolderId(folder._id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                              selectedFolderId === folder._id
                                ? 'border-purple-500/60 bg-purple-500/15'
                                : 'border-white/10 bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: folder.color || '#6a1fbf' }}
                            />
                            <FolderOpen
                              className="w-4 h-4 flex-shrink-0"
                              style={{ color: folder.color || '#6a1fbf' }}
                            />
                            <span className="flex-1 text-sm text-left text-white truncate">
                              {folder.name}
                            </span>
                            {selectedFolderId === folder._id && (
                              <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          setNewFolderName('');
                          setNewFolderColor('#6a1fbf');
                          setStep('new-folder');
                        }}
                        className="mt-3 flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        <FolderPlus className="w-4 h-4" />
                        + Create a new folder
                      </button>
                    </>
                  )}
                </>
              )}

              {step === 'new-folder' && (
                <>
                  {folders.length > 0 ? (
                    <button
                      onClick={() => setStep('choose-folder')}
                      className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white mb-4 transition-colors"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      Back to folder list
                    </button>
                  ) : (
                    <div className="flex items-start gap-2 mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <FolderPlus className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-200">
                        You don't have any folders yet. Create one to save this element.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Folder name</label>
                      <input
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="e.g. Imported Elements"
                        maxLength={100}
                        autoFocus
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Color</label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {FOLDER_COLORS.map((c) => (
                          <button
                            key={c}
                            onClick={() => setNewFolderColor(c)}
                            className={`w-7 h-7 rounded-full transition-all ${
                              newFolderColor === c
                                ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0d0d1a] scale-110'
                                : 'hover:scale-105'
                            }`}
                            style={{ backgroundColor: c }}
                            title={c}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setStep('code')}
                disabled={busy}
                className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                Back
              </button>

              {step === 'choose-folder' && (
                <button
                  onClick={handleCopy}
                  disabled={!selectedFolderId || busy || folders.length === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copying ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {copying ? 'Copying…' : 'Copy Here'}
                </button>
              )}

              {step === 'new-folder' && (
                <button
                  onClick={handleCreateFolderAndCopy}
                  disabled={!newFolderName.trim() || busy}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? <Loader className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
                  {busy ? 'Creating…' : 'Create Folder & Copy'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
